import { useState, useEffect } from 'react';
import { socialService, SocialConnection } from '../services/socialService';
import { useToast } from './use-toast';
import { useQueryClient } from '@tanstack/react-query';

export const useSocialConnections = () => {
  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [disconnectingPlatforms, setDisconnectingPlatforms] = useState<Set<string>>(new Set());
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchConnections = async () => {
    try {
      setIsLoading(true);
      const data = await socialService.getConnections();
      setConnections(data);
      setError(null);
    } catch (error) {
      setError(error as Error);
      console.error('Error fetching connections:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  const handleConnect = async (provider: string) => {
    try {
      // Show requirements modal for Instagram before connecting
      if (provider === 'instagram') {
        setShowInstagramModal(true);
        return;
      }
      
      // Ensure client ID is stored before redirecting to OAuth
      const currentClientId = localStorage.getItem('current_client_id');
      if (!currentClientId) {
        toast({
          title: 'No Brand Selected',
          description: 'Please select a brand/client before connecting social accounts.',
          variant: 'destructive',
        });
        return;
      }
      
      // Store client ID in sessionStorage as backup (persists across OAuth redirect)
      sessionStorage.setItem('oauth_client_id', currentClientId);
      console.log(`🔗 Storing client ID for OAuth: ${currentClientId}`);
      
      let auth_url = '';
      if (provider === 'facebook') {
        ({ auth_url } = await socialService.connectFacebook());
      } else if (provider === 'instagram') {
        ({ auth_url } = await socialService.connectInstagram());
      } else if (provider === 'twitter') {
        ({ auth_url } = await socialService.connectTwitter());
      } else if (provider === 'linkedin') {
        ({ auth_url } = await socialService.connectLinkedIn());
      } else if (provider === 'youtube') {
        ({ auth_url } = await socialService.connectYouTube());
      } else if (provider === 'pinterest') {
        ({ auth_url } = await socialService.connectPinterest());
      }
      window.location.href = auth_url;
    } catch (error) {
      console.error(`Error connecting to ${provider}:`, error);
    }
  };

  const proceedWithInstagramConnection = async () => {
    try {
      // Ensure client ID is stored before redirecting to OAuth
      const currentClientId = localStorage.getItem('current_client_id');
      if (!currentClientId) {
        toast({
          title: 'No Brand Selected',
          description: 'Please select a brand/client before connecting social accounts.',
          variant: 'destructive',
        });
        return;
      }
      
      // Store client ID in sessionStorage as backup (persists across OAuth redirect)
      sessionStorage.setItem('oauth_client_id', currentClientId);
      console.log(`🔗 Storing client ID for OAuth: ${currentClientId}`);
      
      const { auth_url } = await socialService.connectInstagram();
      window.location.href = auth_url;
    } catch (error) {
      console.error('Error connecting to Instagram:', error);
    }
  };

  const handleDisconnect = async (provider: string) => {
    try {
      // Add platform to disconnecting set for loading state
      setDisconnectingPlatforms(prev => new Set(prev).add(provider));
      
      // Optimistically remove from connections for immediate UI feedback
      setConnections(prev => prev.filter(conn => conn.provider !== provider));
      
      // Actually disconnect from backend
      await socialService.deleteConnection(provider);
      
      console.log(`✅ Successfully disconnected from ${provider}`);
      
      // Show success toast
      toast({
        title: 'Account Disconnected',
        description: `Successfully disconnected your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account. Refresh the page to see updated analytics.`,
        variant: 'default',
      });
      
      // Remove from disconnecting set
      setDisconnectingPlatforms(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider);
        return newSet;
      });
      
      // Refresh connections to ensure consistency
      await fetchConnections();
      
      // Invalidate all analytics queries to refresh dashboard metrics
      queryClient.invalidateQueries({ queryKey: ['socialAnalytics'] });
      queryClient.invalidateQueries({ queryKey: ['coreMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['audienceAnalytics'] });
      queryClient.invalidateQueries({ queryKey: ['comments'] });
      
    } catch (error) {
      console.error(`❌ Error disconnecting from ${provider}:`, error);
      
      // Show error toast
      toast({
        title: 'Disconnection Failed',
        description: `Failed to disconnect your ${provider.charAt(0).toUpperCase() + provider.slice(1)} account. Please try again.`,
        variant: 'destructive',
      });
      
      // On error, restore the connection in UI and remove from disconnecting set
      setDisconnectingPlatforms(prev => {
        const newSet = new Set(prev);
        newSet.delete(provider);
        return newSet;
      });
      
      // Refresh connections to restore correct state
      await fetchConnections();
      
      throw error;
    }
  };

  const isConnected = (provider: string) => {
    return connections.some((connection) => connection.provider === provider);
  };

  const isDisconnecting = (provider: string) => {
    return disconnectingPlatforms.has(provider);
  };

  return {
    connections,
    isLoading,
    error,
    fetchConnections,
    handleConnect,
    handleDisconnect,
    isConnected,
    isDisconnecting,
    disconnectingPlatforms,
    showInstagramModal,
    setShowInstagramModal,
    proceedWithInstagramConnection,
  };
};
